// EXAMS-SEND-2026-10-16 §2: one Send dialog for an exam link and an onboarding link -- Copy, Text, Email, any of them.
//   Text  the agents window's /api/staff-text (CRM session, staff allowlist, quiet hours with "send anyway", STOP, one
//         send per click). Their route texts a LEAD by id; a candidate with a matching lead (same number, else same
//         email) goes by that lead; one without goes by candidate_id, which their route takes once they add it.
//   Email from Matt through crm-mailer (/api/send-email, lib/email.js) -- never a mail app (EMAIL-CRM-2026-10-22): a
//         link email as kind exam/onboarding, a message with no link (an exam result) as kind 'message'.
// Every send is logged on the candidate's timeline (activity_log, type onboarding_send): who, when, which channel,
// which link -- by its id, never the link itself (the token is kept only as a hash). An email also logs its own row,
// "Email · <subject> · Sent" or "Bounced" (lib/email.js).
import { makeEmailApi, leadsWithAddress, isOptedOut, OPT_OUT_DECISIONS } from './email';

export const SEND_LOG_TYPE = 'onboarding_send';
export const SUBJECTS = { exam: 'Your ProTherapy competency exam link', onboarding: 'Your ProTherapy onboarding link',
  exam_result: 'Your ProTherapy exam result',   // NIGHT-DIRECTSTAFF-2026-10-19 §3: a result with no link
  credential_expiry: 'Your license or certification expires soon',   // NIGHT-DIRECTSTAFF-2026-10-22 §9: no link
  facility_portal: 'Your ProTherapy facility portal link',             // NIGHT-DIRECTSTAFF-2026-10-23 §2
  offer_summary: 'Your ProTherapy offer',                               // NIGHT-DIRECTSTAFF-2026-10-23c §3
  availability: 'When and where would you like to work next?',         // NIGHT-DIRECTSTAFF-2026-10-24 §1
  profile: 'Your ProTherapy profile link',                            // DAY-DIRECTSTAFF-2026-10-30b §1
  document_request: 'Documents for your ProTherapy onboarding' };      // NIGHT-DIRECTSTAFF-2026-10-29 §4
const KIND_WORDS = { exam: 'competency exam', onboarding: 'onboarding' };
const digits = (v) => String(v || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');

export function firstName(name) {
  const f = String(name || '').trim().split(/\s+/)[0] || '';
  return /^[A-Za-z][A-Za-z'.-]*$/.test(f) ? f : '';
}

/** The prefilled message -- the same words for the text and the email. */
export function messageFor(kind, name, link) {
  const hi = firstName(name) ? `Hi ${firstName(name)},` : 'Hi,';
  return `${hi} here's your ProTherapy ${KIND_WORDS[kind]} link: ${link}. It takes about 20 minutes and you can stop and resume. Reply here with any questions.`;
}

/** "Exam link 12" / "Onboarding link (session 34)": which link, by id. */
export function linkLabel(kind, ref) {
  if (kind === 'exam_result') return `Exam result (attempt ${ref})`;
  if (kind === 'credential_expiry') return `Expiry reminder (document ${ref})`;
  if (kind === 'document_request') return `Document request (candidate ${ref})`;   // NIGHT-DIRECTSTAFF-2026-10-29 §4
  return kind === 'exam' ? `Exam link ${ref}` : `Onboarding link (session ${ref})`;
}
// 'mailto' stays only to read rows logged before EMAIL-CRM-2026-10-22 (the mail-app fallback is gone).
export const CHANNEL_WORDS = { text: 'text', email: 'email', mailto: 'email (from your mail app)', copy: 'copied link' };
export function sendNote(kind, ref, channel, by) {
  return `${linkLabel(kind, ref)} sent by ${CHANNEL_WORDS[channel] || channel}${by ? ` by ${by}` : ''}`;
}
const NOTE_RE = /^(Exam link (\d+)|Onboarding link \(session (\d+)\)) sent by (text|email \(from your mail app\)|email)(?= by |$)/;

/** activity_log rows -> Map("exam:12" | "onboarding:34" -> Set of channels: text, email, mailto). */
export function sentChannels(rows) {
  const m = new Map();
  for (const r of rows || []) {
    const x = NOTE_RE.exec(String(r.note || ''));
    if (!x) continue;
    const key = x[2] ? `exam:${x[2]}` : `onboarding:${x[3]}`;
    const ch = x[4] === 'text' ? 'text' : x[4] === 'email' ? 'email' : 'mailto';
    if (!m.has(key)) m.set(key, new Set());
    m.get(key).add(ch);
  }
  return m;
}
/** "Sent by text and email" (or whichever); '' when nothing was sent. */
export function sentByText(channels) {
  const c = channels ? [...channels] : [];
  const words = [];
  if (c.includes('text')) words.push('text');
  if (c.includes('email')) words.push('email');
  if (c.includes('mailto') && !c.includes('email')) words.push('email (mail app)');
  return words.length ? `Sent by ${words.join(' and ')}` : '';
}

/** The candidate's lead for texting: same phone first, else same email; newest first. -> { id, sms_opt_out } | null */
export function matchLead(candidate, leads) {
  const p = digits(candidate && candidate.phone);
  const e = String((candidate && candidate.email) || '').trim().toLowerCase();
  const newest = [...(leads || [])].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  if (p.length === 10) {
    const byPhone = newest.find((l) => digits(l.phone) === p);
    if (byPhone) return byPhone;
    return null;                      // a candidate with a number is texted at THAT number, never at another lead's
  }
  return (e && newest.find((l) => String(l.email || '').trim().toLowerCase() === e)) || null;
}

/** supabase + staff (supabase.js) -> the dialog's sends. */
export function makeSendApi({ supabase, staff, fetchFn = (...a) => fetch(...a) }) {
  const mailer = makeEmailApi({ supabase, staff });
  return {
    async findLead(candidate) {
      const { data, error } = await supabase.from('leads').select('id,phone,email,sms_opt_out,sms_consent,created_at').order('created_at', { ascending: false }).limit(2000);
      if (error) return null;
      return matchLead(candidate, data);
    },
    // -> { sent, duplicate } ; throws Error with .code (quiet_hours, opted_out, invalid_number, candidate_text_not_supported, ...)
    async text(candidate, body, sendAnyway = false) {
      const lead = await this.findLead(candidate);
      if (lead) return staff.textLead(lead.id, body, sendAnyway);
      try {
        return await staff.textCandidate(candidate.id, body, sendAnyway);
      } catch (e) {
        // their route does not take candidate_id yet: it answers bad_request asking for lead_id
        if (e && e.code === 'bad_request') {
          const err = new Error('Texting someone with no lead record is not switched on yet. Copy the link or email it for now.');
          err.code = 'candidate_text_not_supported';
          throw err;
        }
        throw e;
      }
    },
    async emailReady() {
      try {
        const res = await fetchFn('/api/send-email', { method: 'GET' });
        const out = await res.json();
        return !!(res.ok && out && out.ready === true);
      } catch { return false; }
    },
    // EMAIL-CRM-2026-10-22: through lib/email.js -- logs "Email · <subject> · Sent" (or Bounced + No email on a 409)
    email: (kind, to, body, candidateId = null) => mailer.send({ to, body, candidateId,
      kind: kind === 'exam' || kind === 'onboarding' ? kind : 'message', subject: SUBJECTS[kind] || 'A message from ProTherapy Staffing' }),
    /** Is this person "No email"? (a lead with their address opted out, bounced or unsubscribed). Unreadable = false. */
    async optedOut(candidate) {
      const e = String((candidate && candidate.email) || '').trim();
      if (!e) return false;
      try {
        const pattern = e.replace(/[\\%_]/g, (c) => `\\${c}`);
        let r = await supabase.from('leads').select('id,email,email_opt_out').ilike('email', pattern);
        if (r.error) r = await supabase.from('leads').select('id,email').ilike('email', pattern);     // the column is not there yet
        const leads = r.error ? [] : leadsWithAddress(e, r.data);
        if (leads.some(isOptedOut)) return true;
        if (!leads.length) return false;
        const d = await supabase.from('agent_lead_decisions').select('lead_id,decision').in('lead_id', leads.map((l) => String(l.id))).in('decision', [...OPT_OUT_DECISIONS]);
        return !d.error && (d.data || []).length > 0;
      } catch { return false; }
    },
    async log(candidateId, kind, ref, channel, by) {
      if (!by) { try { const { data } = await supabase.auth.getUser(); by = (data && data.user && data.user.email) || ''; } catch { by = ''; } }
      const { error } = await supabase.from('activity_log').insert({ candidate_id: candidateId, date: new Date().toISOString().split('T')[0],
        type: SEND_LOG_TYPE, note: sendNote(kind, ref, channel, by) });
      if (error) throw new Error(`Sent, but not logged on the timeline: ${error.message}`);
    },
    async sentChannels(candidateIds = null) {
      let q = supabase.from('activity_log').select('candidate_id,note,created_at').eq('type', SEND_LOG_TYPE);
      if (candidateIds) q = q.in('candidate_id', candidateIds);
      const { data, error } = await q;
      return error ? new Map() : sentChannels(data);
    },
  };
}

/** NIGHT-DIRECTSTAFF-2026-10-17 §3: the exam reminder -- short, the exams by name, the link. */
export function reminderMessage(name, link, examNames = []) {
  const hi = firstName(name) ? `Hi ${firstName(name)},` : 'Hi,';
  const which = examNames.length ? ` (${examNames.join(', ')})` : '';
  return `${hi} a quick reminder to finish your ProTherapy competency exams${which}: ${link}. You can pick up where you left off. Reply here with any questions.`;
}

/**
 * NIGHT-DIRECTSTAFF-2026-10-21 §5: may this candidate be texted? true = their matching lead ticked the box and never said
 * STOP; false = their lead says no (unticked, or STOP); null = no lead to ask (unknown -- Email first, Text still allowed).
 */
export function textConsentOf(candidate, leads) {
  const l = matchLead(candidate, leads);
  if (!l) return null;
  return l.sms_consent === true && l.sms_opt_out !== true;
}
